import * as React from 'react';
import { FC, MouseEvent } from 'react';
import { CalendarContainer, default as ReactDatePicker } from 'react-datepicker';
import { format } from 'date-fns';
import {
  Black,
  Blue10,
  Blue80,
  ButtonIcon,
  Gray10,
  Gray100,
  Gray20,
  Gray50,
  typeCompact12Medium,
  typeCompact14Medium,
  White,
} from '@traceair/tds';
import { classes, style, stylesheet } from 'typestyle';
import CloseTinyIcon from '../../../../images/components/CloseTinyIcon';
import CalendarIcon from '../../../../images/components/CalendarIcon';
import { ChevronLeftIcon, ChevronRightIcon } from '@traceair/webapp-icons';

type DocDatePickerProps = {
  date?: Date;
  placeholder?: string;
  onChange: (date?: Date) => void;
  maxDate?: Date;
  minDate?: Date;
  isDisabled?: boolean;
};

const DocDatePicker: FC<DocDatePickerProps> = ({ date, placeholder, onChange, maxDate, minDate, isDisabled }) => {
  function handleChange(date?: Date | null) {
    onChange(date || undefined);
  }

  function clear(event: MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
    onChange();
  }

  const DateContainer = React.forwardRef<HTMLInputElement, { onClick?: () => void }>(({ onClick }, ref) => (
    <>
      <div
        className={classes(pickerRootStyles.common, isDisabled ? pickerRootStyles.disabled : pickerRootStyles.active)}
        onClick={isDisabled ? undefined : onClick}
        ref={ref}
      >
        {date ? (
          <>
            <div>
              <div className={typeCompact12Medium} style={{ color: Gray100 }}>
                {placeholder}
              </div>
              <div className={typeCompact14Medium} style={{ color: isDisabled ? Gray100 : Black, marginTop: 2 }}>
                {format(date, 'd MMM yyyy')}
              </div>
            </div>
            <div
              className={classes(buttonStyles.common, isDisabled ? buttonStyles.disabled : buttonStyles.active)}
              onClick={isDisabled ? undefined : clear}
            >
              <CloseTinyIcon color={isDisabled ? Gray100 : Blue80} />
            </div>
          </>
        ) : (
          <>
            <div className={classes(typeCompact14Medium)} style={{ color: Gray100 }}>
              {placeholder}
            </div>
            <CalendarIcon color={isDisabled ? Gray100 : Black} />
          </>
        )}
      </div>
    </>
  ));

  DateContainer.displayName = 'DateContainer';

  // eslint-disable-next-line react/prop-types
  const MyContainer = ({ className, children }) => {
    return (
      <CalendarContainer className={className} showPopperArrow={false}>
        {children}
      </CalendarContainer>
    );
  };

  // eslint-disable-next-line react/prop-types
  const Header = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
    <div className={headerStyle}>
      <div className={typeCompact14Medium} style={{ color: Black }}>
        {format(date, 'MMMM yyyy')}
      </div>
      <div style={{ display: 'flex' }}>
        <ButtonIcon
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          style="secondary"
          icon={<ChevronLeftIcon color={Blue80} />}
          size="xs"
        />
        <div style={{ marginLeft: 4 }}>
          <ButtonIcon
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            style="secondary"
            icon={<ChevronRightIcon color={Blue80} />}
            size="xs"
          />
        </div>
      </div>
    </div>
  );

  return (
    <ReactDatePicker
      selected={date}
      onChange={handleChange}
      customInput={<DateContainer />}
      calendarContainer={MyContainer}
      renderCustomHeader={Header}
      maxDate={maxDate}
      minDate={minDate}
      useWeekdaysShort={true}
      popperPlacement="bottom-start"
      popperModifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, -6],
          },
        },
      ]}
    />
  );
};

const pickerRootStyles = stylesheet({
  common: {
    height: 40,
    padding: '0 12px',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 12,
    border: `1.5px solid ${Gray50}`,
    whiteSpace: 'nowrap',
  },
  active: {
    cursor: 'pointer',
    backgroundColor: White,
  },
  disabled: {
    backgroundColor: Gray10,
  },
});

const buttonStyles = stylesheet({
  common: {
    width: 24,
    height: 24,
    borderRadius: 8,
    marginLeft: 8,
  },
  active: {
    backgroundColor: Blue10,
    cursor: 'pointer',
  },
  disabled: {
    backgroundColor: Gray20,
  },
});

const headerStyle = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 12px',
});

export default DocDatePicker;
